import { useSelector } from "react-redux";
import { selectCloudName, selectNewTlsDomain } from "root/selectors";
import type { IStockState } from "../data/types";

const selectAuth = (state: IStockState) => ({ isSignedIn: state.istock.isSignedIn, isAuthInProgress: state.istock.isAuthInProgress });
export const useAuth = () => useSelector(selectAuth);

const selectSearchParams = (state: IStockState) => ({
	searchPhrase: state.istock.searchPhrase,
	mediaType: state.istock.mediaType,
	sortOrder: state.istock.sortOrder,
});
export const useSearchParams = () => useSelector(selectSearchParams);

export const selectAssets = (state: IStockState) => ({
	items: state.istock.list.items,
	assetsInProgress: state.istock.list.assetsInProgress,
	page: state.istock.list.page,
	hasMoreAssets: state.istock.list.hasMoreAssets,
	selected: state.istock.list.selected,
});
export const useAssets = () => useSelector(selectAssets);

const selectUI = (state: IStockState) => ({
	isFiltersPanelOpen: state.istock.isFiltersPanelOpen,
	areFiltersSet: state.istock.areFiltersSet,
	purchaseInProgress: state.istock.purchaseInProgress,
});

export const useUI = () => useSelector(selectUI);

export const selectHomePageAssets = (state: IStockState) => ({
	items: state.istock.homePageItems,
	assetsInProgress: state.istock.list.assetsInProgress,
	selected: state.istock.list.selected,
});
export const useHomePageAssets = () => useSelector(selectHomePageAssets);

export const useCloudName = () => useSelector(selectCloudName);

export const useNewTlsDomain = () => useSelector(selectNewTlsDomain);

const selectIStockServer = (state: IStockState) => (state.widgetConfig.istockServer);
export const useIStockServer = () => useSelector(selectIStockServer);

const selectFilterParams = (state: IStockState) => ({
	...state.istock.filters
});
export const useFilterParams = () => useSelector(selectFilterParams);

export const selectSelectedAsset = (state: IStockState) => ({
	selectedFormat: state.istock.selectedFormat,
	selectedAsset:
		state.istock.list.items.find((a) => a.id === state.istock.list.selected) ??
		state.istock.homePageItems.find((a) => a.id === state.istock.list.selected),
	purchasedUrl: state.istock.purchasedUrl
});

export const useSelectedAsset = () => useSelector(selectSelectedAsset);

export const selectProducts = (state: IStockState) => ({
	products: state.istock.products,
	selectedProduct: state.istock.selectedProduct
});
export const useProducts = () => useSelector(selectProducts);

const selectPurchaseInfo = (state: IStockState) => {
	const { selectedAsset } = selectSelectedAsset(state);

	const isAssetIncludedInProduct = selectedAsset?.productTypes.includes(state.istock.selectedProduct?.type);
	const canPurchase = Boolean(state.istock?.selectedProduct && !state.istock.selectedProduct?.exceeded && state.istock.selectedFormat);
	const isAlreadyPurchased = state.istock.downloads
		.filter((item) => selectedAsset?.mediaType.includes(item.mediaType))
		.map((item) => item.id)
		.includes(selectedAsset?.id);

	return {
		isAlreadyPurchased,
		canPurchase: canPurchase && isAssetIncludedInProduct,
		isAssetIncludedInProduct,
		canUpload: Boolean((canPurchase || isAlreadyPurchased && state.istock.selectedFormat ) && state.istock?.purchasedUrl),
	};
};

export const usePurchaseInfo = () => useSelector(selectPurchaseInfo);

export const selectDownloadedAssets = (state: IStockState) => ({
	downloads: state.istock.downloads,
	shouldDownloadsBeRefreshed: state.istock.shouldDownloadsBeRefreshed,
});

const selectAccountInfo = (state: IStockState) => ({
	userName: [state.istock.account.firstName, state.istock.account.lastName].join(" ")
});

export const useAccountInfo = () => useSelector(selectAccountInfo);
